import * as Sentry from '@sentry/react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Route-level Error Boundary for React Router
 * Catches errors that occur in route components and reports them to Sentry
 */
export function RouteErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    // Send error to Sentry
    if (error) {
      if (isRouteErrorResponse(error)) {
        // This is a React Router error response (404, etc.)
        Sentry.captureMessage(`Route Error: ${error.status} ${error.statusText}`, {
          level: 'warning',
          contexts: {
            router: {
              status: error.status,
              statusText: error.statusText,
              data: error.data,
            },
          },
          tags: {
            error_type: 'route_error',
            status: error.status.toString(),
          },
        });
      } else {
        // This is a regular JavaScript error
        Sentry.captureException(error, {
          contexts: {
            router: {
              error_type: 'component_error',
              error_message: error instanceof Error ? error.message : String(error),
            },
          },
          tags: {
            error_type: 'route_component_error',
          },
          level: 'error',
        });
      }
    }
  }, [error]);

  // Handle different error types
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full space-y-6 text-center">
            <div className="flex justify-center">
              <div className="rounded-full bg-muted p-4">
                <AlertTriangle className="h-12 w-12 text-muted-foreground" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Page Not Found
              </h1>
              <p className="text-muted-foreground">
                The page you're looking for doesn't exist.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Other route errors (500, etc.)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full space-y-6 text-center">
          <div className="flex justify-center">
            <div className="rounded-full bg-destructive/10 p-4">
              <AlertTriangle className="h-12 w-12 text-destructive" />
            </div>
          </div>
          
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {error.status}: {error.statusText}
            </h1>
            <p className="text-muted-foreground">
              Something went wrong loading this page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // JavaScript/Component errors
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
  const isDevelopment = import.meta.env.DEV;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full space-y-6 text-center">
        <div className="flex justify-center">
          <div className="rounded-full bg-destructive/10 p-4">
            <AlertTriangle className="h-12 w-12 text-destructive" />
          </div>
        </div>
        
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Something went wrong
          </h1>
          <p className="text-muted-foreground">
            We've been notified and are working to fix the issue.
          </p>
        </div>

        {isDevelopment && (
          <div className="p-4 bg-muted rounded-lg text-left">
            <p className="text-sm font-mono text-muted-foreground break-all">
              {errorMessage}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

